/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import { useTheme } from 'emotion-theming';
import Text from '../../../../shared/components/Text/Text';
import { focusFill, FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setIsStaticAddColumnDropdownMenuOpen, setActiveSecondLayerModal } from '../../ducks/calendarActions';

const DropdownIcon = styled('div')`
  padding: 4px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const DropdownContainerStatic = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  ${focusFill}
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 4px 4;
  background-color: ${({ theme }) => theme.colors.borderLight};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  width: 100%;
`;

const DropdownHeader = styled('div')`
  background-color: ${({ theme }) => theme.colors.surface};
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  padding: 7px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0px;
  font-size: 16px;
`;

const AddPropContainer = styled('div')`
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0px;
  font-size: 16px;
`;

// Create a new HoverableMenuItem component similar to CalendarItemDropdownOptions
const HoverableMenuItem = ({
  item, onClick, id, isFocused,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { colors } = useTheme();
  return (
    <MenuItem
      id={id}
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 14,
        paddingRight: 7,
        cursor: 'pointer',
        outline: 'none',
        /* isFocused is the keyboard cursor, driven by the parent's index rather than by DOM
           focus -- see the window listener there for why. Hover stays grey. */
        background: isFocused
          ? FOCUS_FILL_VAR
          : (isHovered ? colors.surfaceSecondary : 'transparent'),
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      /* A menu is one stop in the tab order and its rows are reached with the arrow keys, so
         these are never tab stops. */
      role='menuitem'
      tabIndex={-1}
    >
      <IconContainer>
        {item.icon ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IcomoonReact
              iconSet={iconSet}
              size={18}
              icon={item.icon}
              color={colors.textSecondary}
              style={{ marginRight: 5 }}
            />
            <MenuText>{item.label}</MenuText>
          </div>
        ) : (
          <ArrowDiv>
            <MenuText>{item.label}</MenuText>
          </ArrowDiv>
        )}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  /** Whether this row is the keyboard cursor. Driven by the parent's index, not DOM focus. */
  isFocused: PropTypes.bool,
};

HoverableMenuItem.defaultProps = {
  isFocused: false,
};

const capWords = (str) => str
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const StaticAddColumnDropdownSimple = ({
  size,
  primaryRows,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  setIntensityUnit,
  setPrimaryRowSwitchState,
  assignedId,
  trigger,
  onPropertyAdded,
}) => {
  const tooltipId = `dropdownIcon-${assignedId}`;
  const dispatch = useDispatch();
  const { colors } = useTheme();

  /** Controlled open state. The item handlers below call stopPropagation to
   * keep the click off the parent, which also stops rc-dropdown's own
   * outside-click close from ever firing — so the menu has to be closed
   * explicitly on select. Without this it stays open over the table, and in
   * the transposed layout (trigger in the footer, menu flipping upward) it
   * covers the property rows and swallows every SwapPropDropdown click.
   * Mirrors StaticAddColumnDropdownComponent's `closeOnSelect`. */
  const [dropdownVisible, setDropdownVisible] = useState(false);

  /** Close explicitly. Keeps the Redux flag in step, because closing this way
   * bypasses `onVisibleChange` — and a flag stuck on `true` would leave the
   * calendar's DraggableContentWrapper/DraggableDivWrapper skipping their
   * flush-on-mouse-leave long after this modal is gone. */
  const closeDropdown = () => {
    setDropdownVisible(false);
    dispatch(setIsStaticAddColumnDropdownMenuOpen(false));
  };

  /** Open explicitly, for the keyboard path. Same reasoning as closeDropdown: rc-dropdown's
   * `onVisibleChange` only fires for its own click trigger, so opening this way has to keep
   * the Redux flag in step itself. */
  const openDropdown = () => {
    setDropdownVisible(true);
    dispatch(setIsStaticAddColumnDropdownMenuOpen(true));
  };

  const triggerRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const menuItemId = (index) => `add-prop-${assignedId}-item-${index}`;

  /** The selection itself, with no event to depend on, so the keyboard path can reuse it. */
  const selectProperty = (row) => {
    const flippedStatus = !primaryRowSwitch[row.name];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[row.name] = flippedStatus;

    if (row.name === 'intensity (bpm)' && setIntensityUnit) {
      setIntensityUnit('bpm');
    }
    if (!flippedStatus) {
      setPrimaryRowSwitch(newSwitch);
    } else if (row.name === 'peak power' || row.name === 'velocity') {
      setPrimaryRowSwitch(newSwitch);
    } else {
      setPrimaryRowSwitchState(newSwitch);
    }
    closeDropdown();
    if (flippedStatus && onPropertyAdded) onPropertyAdded(row.name);
  };

  /** rc-menu's onClick hands over its own event shape; keep the stopPropagation here so the
   * click never reaches the parent, and share the selection itself with the keyboard path. */
  const handleMenuItemClick = (row, e) => {
    e.domEvent.stopPropagation();
    selectProperty(row);
  };

  const handleAboutPropertiesClick = () => {
    closeDropdown();
    // Open as a second-layer modal so the underlying UpdateFutureOccurrencesModal stays open.
    dispatch(setActiveSecondLayerModal('aboutExercisePropertiesModal'));
  };

  /* The selectable rows in render order, so keyboard order and rendered order cannot drift.
     Only the properties still hidden appear, matching the render filter below; About
     Properties is always last. navRowIndexes lets the render look up a row's nav index
     without threading a running counter through the JSX. */
  const hiddenRows = primaryRows.filter((row) => !primaryRowSwitch[row.name]);

  const navItems = [
    ...hiddenRows.map((row) => ({ action: () => selectProperty(row) })),
    { action: handleAboutPropertiesClick },
  ];

  const navRowIndexes = hiddenRows.reduce(
    (acc, row, index) => ({ ...acc, [row.name]: index }),
    {},
  );

  useEffect(() => {
    if (dropdownVisible) {
      setFocusedIndex(0);
    }
  }, [dropdownVisible]);

  /* Navigation is driven by an index on a window listener rather than by moving DOM focus.
     rc-dropdown portals the menu out to document.body and mounts it a commit later, so focus
     never reliably lands inside it -- the arrow keys would reach nothing. Focus stays on the
     trigger and `aria-activedescendant` reports which row is current. Same approach as
     SwapPropDropdown, which drives these same tables. */
  useEffect(() => {
    if (!dropdownVisible) {
      return undefined;
    }
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, navItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        navItems[focusedIndex]?.action();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown();
        triggerRef.current?.focus();
      } else if (e.key === 'Tab') {
        /* No preventDefault: focus is still on the trigger, so the browser moves on from
           there naturally. Closing stops an orphaned menu being left open behind it. */
        closeDropdown();
      }
    };
    window.addEventListener('keydown', handler);
    /* Tearing this down matters: a listener left attached after close would let a later stray
       Enter re-fire whichever row was last highlighted. */
    return () => window.removeEventListener('keydown', handler);
  }, [dropdownVisible, focusedIndex, navItems.length]);

  const menu = (
    <Menu
      style={{
        width: 280,
        minHeight: 100,
        overflowY: 'auto', /** Scroll when the content exceeds the height */
        marginBottom: '0px',
        border: `1px solid ${colors.borderLight}`,
        backgroundColor: colors.surfaceElevated,
      }}
    >
      <AddPropContainer onClick={(e) => e.stopPropagation()}>
        <DropdownHeader>
          <Text fontWeight={700} fontSize='16px'>
            Add Property
          </Text>
          <Text fontSize='16px' color={colors.textSecondary}>
            ⌘+P
          </Text>

        </DropdownHeader>
      </AddPropContainer>
      <MenuWrapper role='menu' style={{ gap: 4 }}>
        {/* A running index so the rendered rows line up with navItems, which the keyboard
            handler indexes into -- the shown properties render as null and must not count. */}
        {primaryRows.map((primaryRow, index) => (!primaryRowSwitch[primaryRow.name] ? (
          <HoverableMenuItem
            key={`first-${primaryRow.name}-${index}`}
            id={menuItemId(navRowIndexes[primaryRow.name])}
            isFocused={navRowIndexes[primaryRow.name] === focusedIndex}
            item={{
              label: primaryRow.name === '%pr' ? '%PR' : capWords(primaryRow.name.includes('intensity') ? 'Intensity' : primaryRow.name),
            }}
            onClick={(e) => handleMenuItemClick(primaryRow, e)}
          />
        ) : null))}
        <CustomDivider />
        <HoverableMenuItem
          key='about-properties'
          id={menuItemId(navItems.length - 1)}
          isFocused={focusedIndex === navItems.length - 1}
          item={{
            label: 'About Properties',
            icon: 'info2',
          }}
          onClick={handleAboutPropertiesClick}
        />
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      overlayStyle={{ zIndex: 1101 }}
      aria-haspopup='true'
      visible={dropdownVisible}
      onVisibleChange={(visible) => {
        setDropdownVisible(visible);
        dispatch(setIsStaticAddColumnDropdownMenuOpen(visible));
      }}
    >
      <DropdownContainerStatic
        aria-expanded={dropdownVisible}
        aria-haspopup='menu'
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          /* rc-dropdown only opens on click, so Tab reached this control and then had no way
             to use it. Open on Enter/Space/ArrowDown while closed -- the house pattern from
             SwapPropDropdown. stopPropagation keeps the opening key from also reaching the
             table's own key handling. Flagging the keyboard path is what moves focus into the
             menu, so the arrow keys work straight away. */
          if (!dropdownVisible && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
            e.preventDefault();
            e.stopPropagation();
            openDropdown();
          }
        }}
        ref={triggerRef}
        role='button'
        tabIndex={0}
      >
        {/* A caller can supply its own trigger node (e.g. the transposed
          * layout's dashed "Add Property" pill); otherwise fall back to the
          * round + icon with its tooltip. */}
        {trigger || (
          <>
            <DropdownIcon padding={size} data-tooltip-id={tooltipId}>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='plus'
                color={colors.white}
              />
            </DropdownIcon>
            <ReactTooltip
              id={tooltipId}
              key={tooltipId}
              place='top'
              content='Add Property'
              style={{
                zIndex: 9999,
                backgroundColor: 'rgba(68, 68, 68, 1)',
                color: colors.white,
                fontFamily: 'Nunito Sans',
                fontSize: '13px',
              }}
            />
          </>
        )}
      </DropdownContainerStatic>
    </Dropdown>
  );
};

StaticAddColumnDropdownSimple.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRows: PropTypes.instanceOf(Array).isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  setIntensityUnit: PropTypes.func,
  setPrimaryRowSwitchState: PropTypes.func.isRequired,
  assignedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trigger: PropTypes.node,
  onPropertyAdded: PropTypes.func,
};

StaticAddColumnDropdownSimple.defaultProps = {
  setIntensityUnit: null,
  assignedId: '',
  trigger: null,
  onPropertyAdded: null,
};

export default StaticAddColumnDropdownSimple;
